import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CircleAlert, Pencil, RefreshCw, UserRound, Warehouse } from 'lucide-react';
import './WarehouseConversation.css';
import axios from 'axios';
import LinkifiedText from './LinkifiedText';
import { EmptyState } from '../design/ui';
import { formatShortDateTime } from '../utils/dates';
import { getStatusLabel } from '../constants/ticketStatuses';

export const WAREHOUSE_VALIDATION_STATUSES = ['pending', 'pending_validation'];

// The request's parameters, labelled; any other parameter is listed too
// (data-driven: a field the agent adds is never invisible).
const PARAM_LABELS = {
  warehouse_reference: 'Référence entrepôt',
  tracking_number: 'Numéro de suivi',
  issue_type: 'Type de problème',
  priority: 'Priorité',
  description: 'Description',
  requested_action: 'Action demandée',
  warehouse_location: 'Emplacement',
};

const MESSAGE_STATUS_LABELS = {
  pending: 'En attente de validation',
  pending_validation: 'En attente de validation',
  sent: 'Envoyé',
  received: 'Reçu',
  failed: 'Échec',
  rejected: 'Refusé',
};

const STATUS_TONES = {
  pending: 'warning',
  pending_validation: 'warning',
  waiting_warehouse_response: 'info',
  warehouse_response_received: 'success',
  completed: 'success',
  failed: 'danger',
};

const humanize = (key) => {
  const text = String(key).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Warehouse conversation of a ticket (« Entrepôt » view of the ticket
 * window, EDN API accounts): the request, its parameters, the exchange with
 * the warehouse, and the decision on a request awaiting validation.
 */
const WarehouseConversation = ({ ticketId, onRefresh }) => {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedParams, setEditedParams] = useState({});
  const [editedMessage, setEditedMessage] = useState('');
  const [validating, setValidating] = useState(false);

  const fetchWarehouseConversation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/tickets/warehouse_conversation/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversation(response.data);
    } catch (fetchError) {
      console.error('Failed to fetch warehouse conversation:', fetchError);
      setError(t('ticketWorkspaceUi.warehouse.fetchError', 'La conversation avec l’entrepôt n’a pas pu être chargée.'));
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    if (ticketId) {
      fetchWarehouseConversation();
    }
  }, [ticketId, fetchWarehouseConversation]);

  const paramLabel = (key) => t(`ticketWorkspaceUi.warehouse.params.${key}`, PARAM_LABELS[key] || humanize(key));

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedParams(conversation.interaction_params || {});
    setEditedMessage(conversation.messages?.[0]?.content || '');
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/tickets/warehouse_conversation/${ticketId}/update`,
        {
          interaction_params: editedParams,
          first_message: editedMessage
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setIsEditing(false);
      await fetchWarehouseConversation();
      if (onRefresh) {
        onRefresh();
      }
    } catch (saveError) {
      console.error('Failed to update warehouse conversation:', saveError);
      setError(t('ticketWorkspaceUi.warehouse.updateError', 'Les modifications de la demande n’ont pas pu être enregistrées.'));
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedParams(conversation.interaction_params || {});
    setEditedMessage(conversation.messages?.[0]?.content || '');
  };

  // A request still waiting for the team: `pending` (mail service) or
  // `pending_validation` (drafted on the account's mailbox). The API returns
  // the conversation's `_id`; `conversation_id` is kept for older payloads.
  const conversationId = conversation?.conversation_id || conversation?._id;
  const awaitsValidation = WAREHOUSE_VALIDATION_STATUSES.includes(conversation?.status);

  const handleValidateMessage = async (approved) => {
    if (!conversationId) {
      setError(t('ticketWorkspaceUi.warehouse.noConversationId', 'Aucune conversation entrepôt à valider.'));
      return;
    }

    setValidating(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/logistics/validate-warehouse-message',
        {
          conversation_id: conversationId,
          approved: approved
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchWarehouseConversation();
      if (onRefresh) {
        onRefresh();
      }
    } catch (validationError) {
      console.error('Failed to validate warehouse message:', validationError);
      setError(t('ticketWorkspaceUi.warehouse.validationError', 'La décision n’a pas pu être enregistrée.'));
    } finally {
      setValidating(false);
    }
  };

  const parseWarehouseResponse = (content) => {
    if (!content) return { details: '' };

    // Parse warehouse response content
    const lines = content.split('\n');
    const response = lines
      .filter(line => !line.trim().startsWith('>'))
      .join('\n')
      .trim();

    return {
      details: response || content
    };
  };

  if (loading) {
    return (
      <div className="tkh tkh--loading" aria-busy="true">
        <span className="tkh-sr">{t('ticketWorkspaceUi.warehouse.loading', 'Chargement de la conversation entrepôt…')}</span>
        <span className="u-skeleton tkh-skeleton tkh-skeleton--line" />
        <span className="u-skeleton tkh-skeleton" />
        <span className="u-skeleton tkh-skeleton" />
      </div>
    );
  }

  if (error && !conversation) {
    return (
      <div className="u-banner u-banner--danger" role="alert">
        <CircleAlert strokeWidth={2} aria-hidden="true" />
        <div className="u-banner__body">{error}</div>
        <div className="u-banner__actions">
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchWarehouseConversation}>
            <RefreshCw strokeWidth={1.8} aria-hidden="true" />
            {t('ticketWorkspaceUi.warehouse.retry', 'Réessayer')}
          </button>
        </div>
      </div>
    );
  }

  // If no conversation exists, show empty state
  if (!conversation || (!conversation.messages || conversation.messages.length === 0)) {
    return (
      <EmptyState icon={Warehouse} title={t('ticketWorkspaceUi.warehouse.emptyTitle', 'Aucune demande à l’entrepôt')}>
        {t('ticketWorkspaceUi.warehouse.empty', 'L’agent IA écrit à l’entrepôt quand le ticket le demande ; sa demande apparaîtra ici.')}
      </EmptyState>
    );
  }

  const displayParams = Object.entries(conversation.interaction_params || {})
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '' && typeof value !== 'object');
  const statusLabel = conversation.status
    ? t(`ticketWorkspaceUi.warehouse.conversationStatus.${conversation.status}`, getStatusLabel(t, conversation.status))
    : '';
  const statusTone = STATUS_TONES[conversation.status];

  return (
    <div className="warehouse-conversation tkh">
      <div className="tkh-head">
        <span className="tkh-head__title">
          <Warehouse strokeWidth={1.8} aria-hidden="true" />
          {t('ticketWorkspaceUi.warehouse.title', 'Demande à l’entrepôt')}
        </span>
        {statusLabel && (
          <span className={`u-badge${statusTone ? ` u-badge--${statusTone}` : ''}`}>
            <span className="u-badge__dot" aria-hidden="true" />
            {statusLabel}
          </span>
        )}
      </div>

      {error && (
        <div className="u-banner u-banner--danger" role="alert">
          <CircleAlert strokeWidth={2} aria-hidden="true" />
          <div className="u-banner__body">{error}</div>
        </div>
      )}

      {(displayParams.length > 0 || isEditing) && (
        <section className="tkh-panel" aria-label={t('ticketWorkspaceUi.warehouse.params.title', 'Paramètres de la demande')}>
          {isEditing ? (
            <div className="tkh-params tkh-params--edit">
              {Object.entries(editedParams).map(([key, value]) => (
                <div key={key} className="u-field">
                  <label className="u-label" htmlFor={`tkh-param-${key}`}>{paramLabel(key)}</label>
                  <input
                    id={`tkh-param-${key}`}
                    type="text"
                    value={value || ''}
                    onChange={(e) =>
                      setEditedParams({
                        ...editedParams,
                        [key]: e.target.value
                      })
                    }
                    className="u-input"
                  />
                </div>
              ))}
            </div>
          ) : (
            <dl className="tkh-params">
              {displayParams.map(([key, value]) => (
                <div key={key} className={String(value).length > 32 ? 'is-wide' : undefined}>
                  <dt>{paramLabel(key)}</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
          {!isEditing && conversation.status !== 'completed' && (
            <div className="tkh-panel__actions">
              <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={handleEditClick}>
                <Pencil strokeWidth={1.8} aria-hidden="true" />
                {t('ticketWorkspaceUi.warehouse.edit', 'Modifier la demande')}
              </button>
            </div>
          )}
        </section>
      )}

      <div className="tkh-thread">
        {conversation.messages.map((message, index) => {
          const isReceived = message.direction === 'received';
          const SenderIcon = isReceived ? Warehouse : UserRound;
          return (
            <article
              key={index}
              className={`warehouse-conversation-message tkh-msg tkh-msg--${isReceived ? 'in' : 'out'}`}
            >
              <header className="tkh-msg__head">
                <span className="tkh-msg__who">
                  <SenderIcon strokeWidth={1.8} aria-hidden="true" />
                  {isReceived
                    ? t('ticketWorkspaceUi.warehouse.warehouse', 'Entrepôt')
                    : t('ticketWorkspaceUi.warehouse.us', 'Nous')}
                </span>
                <span className="tkh-msg__time">{formatShortDateTime(message.timestamp)}</span>
              </header>
              <div className="tkh-msg__body">
                {isEditing && index === 0 ? (
                  <textarea
                    value={editedMessage}
                    onChange={(e) => setEditedMessage(e.target.value)}
                    className="u-textarea"
                    aria-label={t('ticketWorkspaceUi.warehouse.message', 'Message à l’entrepôt')}
                    rows={5}
                  />
                ) : (
                  <div className="warehouse-response__details-content">
                    <LinkifiedText text={isReceived ? parseWarehouseResponse(message.content).details : message.content} />
                  </div>
                )}
              </div>
              {message.status && (
                <footer className="tkh-msg__foot">
                  {t(`ticketWorkspaceUi.warehouse.status.${message.status}`, MESSAGE_STATUS_LABELS[message.status] || message.status)}
                </footer>
              )}
            </article>
          );
        })}
      </div>

      {isEditing && (
        <div className="tkh-decision">
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleCancelEdit}>
            {t('ticketWorkspaceUi.warehouse.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={handleSaveEdit}>
            <Check strokeWidth={2} aria-hidden="true" />
            {t('ticketWorkspaceUi.warehouse.save', 'Enregistrer')}
          </button>
        </div>
      )}

      {awaitsValidation && !isEditing && (
        <div className="tkh-decision tkh-decision--awaiting">
          <span className="tkh-decision__hint">
            {t('ticketWorkspaceUi.warehouse.awaiting', 'Rien ne part à l’entrepôt sans votre accord.')}
          </span>
          <button
            type="button"
            className="u-btn u-btn--secondary u-btn--sm"
            onClick={() => handleValidateMessage(false)}
            disabled={validating}
          >
            {t('ticketWorkspaceUi.warehouse.reject', 'Rejeter')}
          </button>
          <button
            type="button"
            className="u-btn u-btn--primary u-btn--sm"
            onClick={() => handleValidateMessage(true)}
            disabled={validating}
          >
            <Check strokeWidth={2} aria-hidden="true" />
            {t('ticketWorkspaceUi.warehouse.approve', 'Approuver et envoyer')}
          </button>
        </div>
      )}
    </div>
  );
};

export default WarehouseConversation;
